import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Flower2, ClipboardList, LogOut, Store, Settings, Star, Images, Wallet, BarChart3, Tags } from "lucide-react";
import { api } from "@/lib/api";
import { useNewOrders } from "@/hooks/useNewOrders";
import RecoveryBanner from "./RecoveryBanner";

export default function AdminLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const newOrders = useNewOrders(!!user);

  useEffect(() => {
    const token = localStorage.getItem("puspita_admin_token");
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("puspita_admin_token");
        navigate("/admin/login", { replace: true });
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("puspita_admin_token");
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#F4EFE6] flex items-center justify-center">
        <p data-testid="admin-loading" className="text-sm text-[#6B7C66]">Memuat panel admin...</p>
      </div>
    );
  }
  if (!user) return null;

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
      isActive ? "bg-[#3D5A40] text-[#FAF7EE]" : "text-[#CBD8C9] hover:bg-[#2A3A2A] hover:text-white"
    }`;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F4EFE6] flex">
      <aside className="w-60 shrink-0 bg-[#1C281C] text-[#FAF7EE] flex flex-col min-h-screen sticky top-0">
        <div className="px-5 py-6 border-b border-[#2D3E2E]">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#3D5A40] flex items-center justify-center font-bold text-sm">
              P
            </span>
            <div>
              <span className="font-serif-display text-base font-bold block leading-none">
                Puspita Florist
              </span>
              <span className="text-[10px] uppercase tracking-widest text-[#8FA88B]">Panel Admin</span>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavLink to="/admin/products" data-testid="admin-nav-products" className={linkCls}>
            <Flower2 className="w-4 h-4" /> Stok Produk
          </NavLink>
          <NavLink to="/admin/categories" data-testid="admin-nav-categories" className={linkCls}>
            <Tags className="w-4 h-4" /> Manajemen Kategori
          </NavLink>
          <NavLink to="/admin/orders" data-testid="admin-nav-orders" className={linkCls}>
            <ClipboardList className="w-4 h-4" /> Pesanan Masuk
            {newOrders.unseen > 0 && (
              <span
                data-testid="admin-new-orders-badge"
                className="ml-auto min-w-[1.5rem] h-6 px-1.5 rounded-full bg-[#E8B4B4] text-[#3A1F1F] text-[11px] font-bold flex items-center justify-center animate-pulse"
              >
                {newOrders.unseen}
              </span>
            )}
          </NavLink>
          <NavLink to="/admin/payments" data-testid="admin-nav-payments" className={linkCls}>
            <Wallet className="w-4 h-4" /> Pembayaran
            {newOrders.pendingPayments > 0 && (
              <span
                data-testid="admin-pending-payments-badge"
                className="ml-auto min-w-[1.5rem] h-6 px-1.5 rounded-full bg-[#E8D9A8] text-[#3A3214] text-[11px] font-bold flex items-center justify-center"
              >
                {newOrders.pendingPayments}
              </span>
            )}
          </NavLink>
          <NavLink to="/admin/reports" data-testid="admin-nav-reports" className={linkCls}>
            <BarChart3 className="w-4 h-4" /> Laporan Pesanan
          </NavLink>
          <NavLink to="/admin/gallery" data-testid="admin-nav-gallery" className={linkCls}>
            <Images className="w-4 h-4" /> Galeri & Foto
          </NavLink>
          <NavLink to="/admin/reviews" data-testid="admin-nav-reviews" className={linkCls}>
            <Star className="w-4 h-4" /> Ulasan Pelanggan
          </NavLink>
          <NavLink to="/admin/settings" data-testid="admin-nav-settings" className={linkCls}>
            <Settings className="w-4 h-4" /> Pengaturan Toko
          </NavLink>
          <a href="/" data-testid="admin-nav-store" className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-[#CBD8C9] hover:bg-[#2A3A2A] hover:text-white transition-colors">
            <Store className="w-4 h-4" /> Lihat Toko
          </a>
        </nav>
        <div className="p-3 border-t border-[#2D3E2E]">
          <p className="text-[11px] text-[#8FA88B] px-2 mb-2 truncate">{user.email}</p>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-[#E8B4B4] hover:bg-[#3A2828] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </div>
      </aside>
      <main className="flex-1 p-6 sm:p-10 max-w-5xl">
        <RecoveryBanner />
        <Outlet context={{ ...newOrders, user, setUser }} />
      </main>
    </div>
  );
}
